import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  TextField
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import axiosClient from '../../../../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';
import BtnLoading from '../../../../../../../components/BasicComponents/Buttons/BtnLoading';

export default function DialogEditarSituacaoFinanceira({
  open,
  situacaoFinanceiraAtual,
  publicoId,
  onClose
}: DialogEditarSituacaoFinanceiraProps) {

  const queryClient = useQueryClient();

  const [situacaoSelecionada, setSituacaoSelecionada] = useState<string>(
    mapearSituacaoParaCodigo(situacaoFinanceiraAtual)
  );
  const [motivo, setMotivo] = useState<string>('');

  //queries e mutations
  const { mutate: editar, isLoading } = useMutation({
    mutationKey: 'editar-situacao-financeira-cooperado',
    mutationFn: (data: { situacao: string; motivo: string }) => 
      axiosClient.put(`/cadastros/editar-situacao-financeira/${publicoId}`, data),
    onError: (error) => {
      console.error(error)
      enqueueSnackbar('Erro ao editar situação financeira!', { variant: 'error' });
    },
    onSuccess: () => {
      enqueueSnackbar(`Situação financeira editada com sucesso`, { variant: "success" });
      queryClient.invalidateQueries('cadastros/detalhar-cooperado')
      onClose()
    }
  })

  const handleEditar = () => {
    editar({ situacao: situacaoSelecionada, motivo });
    onClose();
  };

  const handleClose = () => {
    setSituacaoSelecionada(mapearSituacaoParaCodigo(situacaoFinanceiraAtual));
    setMotivo('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        Editar dados
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel id="situacao-financeira-label">Situação Financeira</InputLabel>
            <Select
              labelId="situacao-financeira-label"
              label="Situação Financeira"
              value={situacaoSelecionada}
              onChange={(e) => setSituacaoSelecionada(e.target.value as string)}

            >
              {situacoesDisponiveis.map((situacao) => (
                <MenuItem key={situacao.value} value={situacao.value}>
                  {`${situacao.label} (${situacao.value})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!publicoId.startsWith('E') && (
            <TextField
              fullWidth
              label="Motivo"
              multiline
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              error={motivo.length > 40}
              helperText={`${motivo.length}/40 caracteres${motivo.length > 40 ? ' - Limite excedido' : ''}`}
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions >
        <Button
          onClick={handleClose}
          variant="outlined"
        >
          Cancelar
        </Button>

        <BtnLoading
          onClick={handleEditar}
          variant="contained"
          color="primary"
          loading={isLoading}
          disabled={
            situacaoSelecionada === mapearSituacaoParaCodigo(situacaoFinanceiraAtual) || 
            (!publicoId.startsWith('E') && motivo.length > 40)
          }
        >
          Editar
        </BtnLoading>
      </DialogActions>
    </Dialog>
  );
}

interface DialogEditarSituacaoFinanceiraProps {
  open: boolean;
  situacaoFinanceiraAtual: string;
  publicoId: string;
  onClose: () => void;
}

const situacoesDisponiveis = [
  { value: 'L', label: 'Liberado' },
  { value: 'S', label: 'Sempre Liberado' },
  { value: 'B', label: 'Pendencias Financeiras' },
  { value: 'D', label: 'Bloqueado Definitivo' },
  { value: 'M', label: 'Bloqueado Manual' }
];

const mapearSituacaoParaCodigo = (situacao: string): string => {
  const mapeamento: { [key: string]: string } = {
    'LIBERADO': 'L',
    'SEMPRE LIBERADO': 'S',
    'PENDENCIAS FINANCEIRAS': 'B',
    'BLOQUEADO MANUAL': 'M',
    'BLOQUEADO DEFINITIVO': 'D'
  };
  return mapeamento[situacao] || 'L';
};